import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

import './adhoc.css';
import Persona from './components/Persona.jsx';
import TemplateDropdown from './components/TemplateDropdown.jsx';
import InputCustomInstruction from './components/InputCustomInstruction.jsx';
import GeneratedPrompt from './components/GeneratedPrompt.jsx';
import Alert from "../components/Alert"
// import SYSTEM_PROMPT from './prompts/SYSTEM_PROMPT.js';
// import GENERATE_PROMPT from './prompts/GENERATE_PROMPT.js';
import PatientsInformation from './components/PatientsInformation.jsx';
import { templateData } from '../data/templateData';


function AdhocTask() {
  const API_URL = process.env.NODE_ENV === 'development' ? "https://demo.aiguardian.io/agent-backend": "/agent-backend";
  const { user, logout } = useAuth();

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isTemplateConfirmed, setIsTemplateConfirmed] = useState(false);

  const [instruction, setInstruction] = useState('');
  const [isInstructionSubmitted, setIsInstructionSubmitted] = useState(false);

  const [inputMessage, setInputMessage] = useState('A prompt will be generated based on your instructions.');
  const [loadingLLMResponse, setLoadingLLMResponse] = useState(false);

  const [isInstructionClear, setIsInstructionClear] = useState(false);
  const [promptTemplate, setPromptTemplate] = useState('');
  const [showSaveTemplateButton, setShowSaveTemplateButton] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  const [promptPlaceholders, setPromptPlaceholders] = useState([]);
  const [patients, setPatients] = useState([]);

  const [showAlert, setShowAlert] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  
  const triggerAlert = (msg) => {
      setAlertMessage(msg);
      setShowAlert(true);
    };


  const handleInstructionChange = (e) => {
    setInstruction(e.target.value);
    setIsInstructionSubmitted(false);
    setLoadingLLMResponse(false);
  };

  const handleInstructionSubmit = async () => {
    setIsInstructionSubmitted(true);
    setLoadingLLMResponse(true);

    // const functionTools = [
    //   {
    //     type: "function",
    //     function: {
    //       name: "generate_prompt_template",
    //       description: GENERATE_PROMPT,
    //       parameters: {
    //         type: "object",
    //         properties: {
    //           prompt: {
    //             type: "string",
    //             description: "The generated prompt template in YAML-format"
    //           }
    //         }
    //       }
    //     }
    //   }
    // ]
    //"https://api.openai.com/v1/chat/completions"
    const response = await fetch(API_URL + '/api/openai_chat', {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${user.token}`
      },
      body: JSON.stringify({
        model: "gpt-4.1-mini-2025-04-14",
        messages: [
          // {
          //   role: "system",
          //   content: SYSTEM_PROMPT
          // },
          {
            role: "user",
            content: instruction
          }
        ],
        // tools: functionTools,
        // tool_choice: "auto"
      })
    });

    const data = await response.json();
    console.log("OpenAI Response:", data);
    setLoadingLLMResponse(false);

    // Check if tool calls happened
    if (data.choices && data.choices[0].message.tool_calls) {
      for (const toolCall of data.choices[0].message.tool_calls) {
        if (toolCall.function.name === "generate_prompt_template") {
          const args = JSON.parse(toolCall.function.arguments);
          handleSetPromptTemplate(args.prompt);
          setIsInstructionClear(true);
        }
      }
    } else {
      // No tool call — probably LLM asking for clarification
      setInputMessage(data.choices[0].message.content);
    }    
  };

  const handleSetPromptTemplate = (prompt) => {
    setPromptTemplate(prompt);
    setPromptPlaceholders(extractPlaceholders(prompt));
  }

  const handlePromptChange = (e) => {
    setPromptTemplate(e.target.value);
    setShowSaveTemplateButton(true);
  };

  const handleSaveTemplate = () => {
    setShowSaveTemplateButton(false);
    setShowDialog(false);
  }

  const extractPlaceholders = (promptTemplate) => {
    if (!promptTemplate) return [];

    const placeholderPattern = /<[^<>]+?>/g;
    const matches = promptTemplate.match(placeholderPattern);

    // Remove duplicates by converting to a Set and back to array
    return matches ? Array.from(new Set(matches)) : [];
  };

  const handleCallSelectedPatients = async (patients, placeholders) => {
    const tasks = patients
      .filter(patient => patient.selected)
      .map(patient => {
        // Create patientPrompt by replacing placeholders
        let patientPrompt = promptTemplate;
        placeholders.forEach(ph => {
          const value = patient[ph] || ""; // Fallback to empty string if undefined
          patientPrompt = patientPrompt.replaceAll(ph, value);
        });

        const newTask = {
          type: "ad-hoc",
          name: patient.name,
          lang: patient.lang,
          phone_number: patient.phone_number,
          prompt: patientPrompt,
          ...(patient.callTime !== "" && { call_at: patient.callTime })
        }
        // Return fetch promise
        return fetch(API_URL + '/api/createjob', {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + user.token
          },
          body: JSON.stringify(newTask)
        }).then(response => {
          if (response.status === 200) {
            return { success: true, name: patient.name };
          } else {
            return { success: false, name: patient.name };
          }
        }).catch(() => {
          return { success: false, name: patient.name };
        });
        });

    // Run all API calls in parallel
    const results = await Promise.all(tasks);

    // Summarize the outcome
    const successCount = results.filter(r => r.success).length;
    const failCount = results.length - successCount;

    //alert(`Created ${successCount} calls successfully. ${failCount} failed.`);
    triggerAlert(`Created ${successCount} calls successfully. ${failCount} failed.`)
  };

  
  return (
    <div className="app-container">
      <div className="left-column">
        <Persona personaData={templateData.find(persona => persona.service === "Adhoc Task")}/>
      </div>
      <div className="right-column">
        <TemplateDropdown 
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          isTemplateConfirmed={isTemplateConfirmed}
          setIsTemplateConfirmed={setIsTemplateConfirmed}
          handleSetPromptTemplate={handleSetPromptTemplate}
        />
        {selectedTemplate === 'Custom' && isTemplateConfirmed && !isInstructionClear && (
          <InputCustomInstruction 
            instruction={instruction}
            handleInstructionChange={handleInstructionChange}
            inputMessage={inputMessage}
            handleInstructionSubmit={handleInstructionSubmit}
            isInstructionSubmitted={isInstructionSubmitted}
            loadingLLMResponse={loadingLLMResponse}
          />
        )}

        {promptTemplate && (
          <>
            <GeneratedPrompt 
              showSaveTemplateButton={showSaveTemplateButton}
              showDialog={showDialog}
              setShowDialog={setShowDialog}
              promptTemplate={promptTemplate}
              handlePromptChange={handlePromptChange}
              handleSaveTemplate={handleSaveTemplate}
            />
            <PatientsInformation 
              patients={patients}
              setPatients={setPatients}
              placeholders={promptPlaceholders}
              handleCallSelectedPatients={handleCallSelectedPatients}
            />
          </>
        )}
      </div>

       <Alert
                    show={showAlert}
                    onClose={() => setShowAlert(false)}
                    message={alertMessage}
                  />
                  
    </div>
  );
}

export default AdhocTask;
